import { UnifiedApiError } from './unified-api-client';
import { logger } from './logger';

// Toast interface that matches our custom toast implementation
interface ToastFunction {
  (toast: {
    title: string;
    description?: string;
    type?: 'success' | 'error' | 'warning' | 'info';
    duration?: number;
  }): void;
}

// Global toast function - will be set by ToastProvider
let globalShowToast: ToastFunction | null = null;

export const setGlobalToastFunction = (toastFunction: ToastFunction) => {
  globalShowToast = toastFunction;
};

export interface ErrorToastOptions {
  title?: string;
  description?: string;
  duration?: number;
  showDetails?: boolean;
}

export class GlobalErrorHandler {
  private static instance: GlobalErrorHandler;
  private enabled: boolean = true;
  private translationFunction:
    | ((key: string, options?: Record<string, unknown>) => string)
    | null = null;

  private constructor() {}

  static getInstance(): GlobalErrorHandler {
    if (!GlobalErrorHandler.instance) {
      GlobalErrorHandler.instance = new GlobalErrorHandler();
    }
    return GlobalErrorHandler.instance;
  }

  /**
   * Set translation function for error messages
   */
  setTranslationFunction(
    t: (key: string, options?: Record<string, unknown>) => string
  ): void {
    this.translationFunction = t;
  }

  /**
   * Enable or disable global error handling
   */
  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
  }

  /**
   * Handle API errors and show toast notifications
   */
  handleError(error: unknown, options?: ErrorToastOptions): void {
    if (!this.enabled) {
      return;
    }

    // Log the error for debugging
    logger.error('Global error handler caught error:', error);

    const t = this.translationFunction;
    let title =
      options?.title || (t ? t('api.errors.unknownError.title') : 'Error');
    let description =
      options?.description ||
      (t
        ? t('api.errors.unknownError.message')
        : 'An unexpected error occurred');
    let severity: 'danger' | 'warning' | 'primary' = 'danger';

    if (error instanceof UnifiedApiError) {
      // Handle API errors with status codes
      title =
        options?.title || (t ? t('api.errors.unknownError.title') : 'Error');

      const status = error.details.status;
      switch (status) {
        case 400:
          title = t ? t('api.errors.badRequest.title') : 'Bad Request';
          description =
            error.details.userMessage ||
            error.message ||
            (t
              ? t('api.errors.badRequest.message')
              : 'The request was invalid');
          severity = 'warning';
          break;
        case 401:
          title = t ? t('api.errors.unauthorized.title') : 'Unauthorized';
          description = t
            ? t('api.errors.unauthorized.message')
            : 'Please log in to access this resource';
          severity = 'warning';
          break;
        case 403:
          title = t ? t('api.errors.forbidden.title') : 'Forbidden';
          description = t
            ? t('api.errors.forbidden.message')
            : 'You do not have permission to access this resource';
          severity = 'warning';
          break;
        case 404:
          title = t ? t('api.errors.notFound.title') : 'Not Found';
          description = t
            ? t('api.errors.notFound.message')
            : 'The requested resource was not found';
          severity = 'warning';
          break;
        case 422:
          title = t ? t('api.errors.validation.title') : 'Validation Error';
          description =
            error.details.userMessage ||
            error.message ||
            (t
              ? t('api.errors.validation.message')
              : 'The provided data is invalid');
          severity = 'warning';
          break;
        case 429:
          title = t ? t('api.errors.rateLimited.title') : 'Too Many Requests';
          description = t
            ? t('api.errors.rateLimited.message')
            : 'Please wait before making another request';
          severity = 'warning';
          break;
        case 500:
          title = t ? t('api.errors.serverError.title') : 'Server Error';
          description = t
            ? t('api.errors.serverError.message')
            : 'An internal server error occurred';
          break;
        case 502:
        case 503:
        case 504:
          title = t
            ? t('api.errors.serviceUnavailable.title')
            : 'Service Unavailable';
          description = t
            ? t('api.errors.serviceUnavailable.message')
            : 'The service is temporarily unavailable. Please try again later';
          break;
        default:
          if (status && status >= 400 && status < 500) {
            title = t ? t('api.errors.clientError.title') : 'Client Error';
            description =
              error.details.userMessage ||
              error.message ||
              (t
                ? t('api.errors.clientError.message', { status })
                : `HTTP ${status}`);
            severity = 'warning';
          } else if (status && status >= 500) {
            title = t ? t('api.errors.serverError.title') : 'Server Error';
            description =
              error.details.userMessage ||
              error.message ||
              (t
                ? t('api.errors.serverError.message')
                : `Server error ${status}`);
          } else {
            description = error.details.userMessage || error.message || description;
          }
      }

      // Add additional error details if available and requested
      if (options?.showDetails && error.details.details) {
        description += `\n\nDetails: ${JSON.stringify(error.details.details, null, 2)}`;
      }
    } else if (error instanceof Error) {
      // Handle general JavaScript errors
      if (
        error.message.includes('Network error') ||
        error.message.includes('Failed to fetch')
      ) {
        title = t ? t('api.errors.networkError.title') : 'Network Error';
        description = t
          ? t('api.errors.networkError.message')
          : 'Unable to connect to the server. Please check your internet connection and try again.';
        severity = 'danger';
      } else if (error.message.includes('AbortError')) {
        title = t
          ? t('api.errors.requestCancelled.title')
          : 'Request Cancelled';
        description = t
          ? t('api.errors.requestCancelled.message')
          : 'The request was cancelled';
        severity = 'primary';
      } else {
        description = error.message;
      }
    }

    // Override with custom options if provided
    title = options?.title || title;
    description = options?.description || description;

    if (globalShowToast) {
      const toastTypeMap: Record<string, string> = {
        danger: 'error',
        warning: 'warning',
        primary: 'info',
      };

      globalShowToast({
        title,
        description,
        type: (toastTypeMap[severity] as 'error' | 'warning' | 'info') || 'error',
        duration: options?.duration || 5000,
      });
    } else {
      // Fallback to console if toast system is not available
      // eslint-disable-next-line no-console
      console.error(`[${severity.toUpperCase()}] ${title}: ${description}`);
    }
  }

  /**
   * Wrap an async function with error handling
   */
  async withErrorHandling<T>(
    fn: () => Promise<T>,
    options?: ErrorToastOptions
  ): Promise<T | null> {
    try {
      return await fn();
    } catch (error) {
      this.handleError(error, options);
      return null;
    }
  }

  /**
   * Create a wrapped version of an async function that handles errors
   */
  wrapAsyncFunction<T extends unknown[], R>(
    fn: (...args: T) => Promise<R>,
    options?: ErrorToastOptions
  ): (...args: T) => Promise<R | null> {
    return async (...args: T): Promise<R | null> => {
      try {
        return await fn(...args);
      } catch (error) {
        this.handleError(error, options);
        return null;
      }
    };
  }
}

// Export singleton instance
export const errorHandler = GlobalErrorHandler.getInstance();

/**
 * Utility function to handle errors globally
 */
export function handleError(error: unknown, options?: ErrorToastOptions): void {
  errorHandler.handleError(error, options);
}

/**
 * Higher-order function to wrap API calls with error handling
 */
export function withErrorHandling<T>(
  apiCall: () => Promise<T>,
  options?: ErrorToastOptions
): Promise<T | null> {
  return errorHandler.withErrorHandling(apiCall, options);
}
